import {
  compact,
  filter,
  find,
  first,
  flatMap,
  forEach,
  isEmpty,
  map,
  orderBy,
  partition,
  some,
} from 'es-toolkit/compat';

import { ExperimentResult, FlowClassName } from 'interfaces/experimentResults';
import { getSecondaryAnalysisJobPrimaryRunOrchestrationId } from 'interfaces/job';
import {
  FeatureMetadata,
  FeatureMetadataBeforeSecondaryAnalysisGrouping,
  FeatureMetadataSecondaryAnalysisEntry,
  FeatureMetadataWithMissingParent,
  FeatureMetadataWithSecondaryAnalysisFlow,
  FeatureMetadataWithSingleRun,
  SecondaryAnalysisStatus,
} from './featureMetadata';

const resultMetadataKeys = [
  'job',
  'experimentResultId',
  'orchestrationId',
  'numFeatures',
  'createdAt',
  'approved',
  'internallyApproved',
  'flowClassName',
  'resultType',
  'primaryRunOrchestrationId',
  'deletedAt',
  'deletedBy',
] as const;

export const composeSecondaryHeatmapsFromPrimary = (
  primaryHeatmaps: FeatureMetadataBeforeSecondaryAnalysisGrouping[],
  primaryResult: ExperimentResult,
  secondaryResult: ExperimentResult
): FeatureMetadataBeforeSecondaryAnalysisGrouping[] => {
  const primaryDisplayName = primaryResult.job?.name || primaryResult.name || primaryResult.job?.id || '';
  const secondaryDisplayName = secondaryResult.job?.name || secondaryResult.name || secondaryResult.job?.id || '';
  const secondaryMetadata = Object.fromEntries(
    map(resultMetadataKeys, (key) => [key, secondaryResult[key]])
  ) as Partial<FeatureMetadataBeforeSecondaryAnalysisGrouping>;
  secondaryMetadata.approved = Boolean(secondaryResult.approved);
  secondaryMetadata.internallyApproved = Boolean(secondaryResult.internallyApproved);
  const primaryIdPrefix = `[${primaryResult.experimentResultId}]-`;
  const secondaryIdPrefix = `[${secondaryResult.experimentResultId}]-`;

  const composeHeatmap = (
    heatmap: FeatureMetadataBeforeSecondaryAnalysisGrouping
  ): FeatureMetadataBeforeSecondaryAnalysisGrouping => ({
    ...heatmap,
    ...secondaryMetadata,
    id: heatmap.id.startsWith(primaryIdPrefix)
      ? `${secondaryIdPrefix}${heatmap.id.slice(primaryIdPrefix.length)}`
      : heatmap.id,
    displayName: heatmap.displayName === primaryDisplayName ? secondaryDisplayName : heatmap.displayName,
    nestedItems: map(heatmap.nestedItems, composeHeatmap),
  });

  return map(primaryHeatmaps, composeHeatmap);
};

export const isRunWithoutSecondaryAnalysis = (result?: FeatureMetadata): result is FeatureMetadataWithSingleRun =>
  (result as { secondaryAnalysisStatus?: SecondaryAnalysisStatus })?.secondaryAnalysisStatus ===
  SecondaryAnalysisStatus.None;

export const isParsedSecondaryAnalysisRun = (
  result: FeatureMetadata & {
    secondaryAnalysisStatus?: SecondaryAnalysisStatus;
  }
): result is FeatureMetadataWithSecondaryAnalysisFlow =>
  result?.secondaryAnalysisStatus === SecondaryAnalysisStatus.PrimaryResult;

export const flattenSecondaryResult = (
  result: FeatureMetadataWithSingleRun | FeatureMetadataWithSecondaryAnalysisFlow
) => compact([result, ...(result?.secondaryResults || [])]);

export const flattenParsedSecondaryResults = (results: FeatureMetadata[]) => flatMap(results, flattenSecondaryResult);

export const getPrimaryRunOrchestrationIdFromExperimentResult = (
  experimentResult?: Partial<ExperimentResult | FeatureMetadata>
): string | undefined =>
  experimentResult?.flowClassName === FlowClassName.CalculateFeatures
    ? experimentResult?.primaryRunOrchestrationId ||
      getSecondaryAnalysisJobPrimaryRunOrchestrationId(experimentResult?.job)
    : undefined;

export const getDefaultSecondaryAnalysisResult = (
  secondaryResultsForPrimary: ExperimentResult[]
): ExperimentResult | undefined => {
  const [deletedResults, availableResults] = partition(
    orderBy(secondaryResultsForPrimary, ['createdAt'], ['desc']),
    'deletedAt'
  );
  return (
    find(availableResults, { approved: true }) ||
    find(availableResults, { internallyApproved: true }) ||
    first(availableResults) ||
    first(deletedResults)
  );
};

export const groupPrimaryResultsWithSecondaryResults = (
  results: FeatureMetadataBeforeSecondaryAnalysisGrouping[]
): FeatureMetadata[] => {
  const [secondaryResults, primaryResults] = partition(
    results,
    (result) => result.flowClassName === FlowClassName.CalculateFeatures && Boolean(result.primaryRunOrchestrationId)
  );
  const getPrimaryResultKey = (result: { key?: string; orchestrationId?: string }) =>
    JSON.stringify([result.orchestrationId, result.key]);
  const getSecondaryParentKey = (result: { key?: string; primaryRunOrchestrationId?: string }) =>
    JSON.stringify([result.primaryRunOrchestrationId, result.key]);
  const getPrimaryNestedResultKey = (result: { key?: string; columnName?: string; orchestrationId?: string }) =>
    JSON.stringify([result.orchestrationId, result.key, result.columnName || '']);
  const getSecondaryNestedResultKey = (result: {
    key?: string;
    columnName?: string;
    primaryRunOrchestrationId?: string;
  }) => JSON.stringify([result.primaryRunOrchestrationId, result.key, result.columnName || '']);
  const primaryResultKeys = new Set<string>(map(primaryResults, (result) => getPrimaryResultKey(result)));
  const secondaryResultsByPrimary = new Map<string, FeatureMetadataBeforeSecondaryAnalysisGrouping[]>();
  forEach(secondaryResults, (result) => {
    const resultKey = getSecondaryNestedResultKey(result);
    const groupedResults = secondaryResultsByPrimary.get(resultKey) || [];
    groupedResults.push(result);
    secondaryResultsByPrimary.set(resultKey, groupedResults);
  });

  const secondaryResultsWithoutPrimary = map(
    filter(
      secondaryResults,
      (result) =>
        result.flowClassName === FlowClassName.CalculateFeatures &&
        Boolean(result.primaryRunOrchestrationId) &&
        !primaryResultKeys.has(getSecondaryParentKey(result))
    ),
    (result) =>
      ({
        ...result,
        secondaryAnalysisStatus: SecondaryAnalysisStatus.SecondaryResultWithMissingParent,
      } as FeatureMetadataWithMissingParent)
  );

  const resultsWithSecondaryAsNested = map(primaryResults, (primaryResult) => {
    const secondaryResultsForPrimary = map(
      primaryResult.orchestrationId
        ? secondaryResultsByPrimary.get(getPrimaryNestedResultKey(primaryResult)) || []
        : [],
      (secondaryResult) => ({
        ...secondaryResult,
        secondaryAnalysisStatus: SecondaryAnalysisStatus.SecondaryResult,
      })
    ) as FeatureMetadataSecondaryAnalysisEntry[];

    if (!isEmpty(secondaryResultsForPrimary)) {
      return {
        ...primaryResult,
        secondaryResults: secondaryResultsForPrimary,
        secondaryAnalysisStatus: SecondaryAnalysisStatus.PrimaryResult,
      } as FeatureMetadataWithSecondaryAnalysisFlow;
    } else {
      return {
        ...primaryResult,
        secondaryResults: undefined,
        secondaryAnalysisStatus: SecondaryAnalysisStatus.None,
      } as FeatureMetadataWithSingleRun;
    }
  });

  const groupedResults: FeatureMetadata[] = [...resultsWithSecondaryAsNested, ...secondaryResultsWithoutPrimary];
  const groupedResultIds = new Set(
    flatMap(groupedResults, (groupedResult) => [groupedResult.id, ...map(groupedResult.secondaryResults, 'id')])
  );

  // Check that all original results are present in the final result
  if (some(results, (originalResult) => !groupedResultIds.has(originalResult.id))) {
    console.warn('Not all original results are present in the final result after grouping secondary results', {
      originalResults: results,
      groupedResults,
    });
  }

  return groupedResults;
};

export const addPrimaryHeatmapsToSecondaryResults = (slideResults: ExperimentResult[]) =>
  map(slideResults, (result) => {
    if (result?.flowClassName !== FlowClassName.CalculateFeatures) {
      return result;
    }
    const primaryRunOrchestration = result.primaryRunOrchestrationId
      ? find(slideResults, { orchestrationId: result.primaryRunOrchestrationId })
      : undefined;
    return {
      ...result,
      options: primaryRunOrchestration ? primaryRunOrchestration?.options : result?.options,
      presentationInfo: primaryRunOrchestration ? primaryRunOrchestration?.presentationInfo : result?.presentationInfo,
    } as ExperimentResult;
  });

export const findPrimaryRunResultAndSecondaryResultsForPrimaryByOrchestrationId = (
  primaryRunOrchestrationId: string | undefined,
  experimentResults: ExperimentResult[]
) => {
  const primaryRunResult =
    primaryRunOrchestrationId &&
    find(experimentResults, {
      orchestrationId: primaryRunOrchestrationId,
      flowClassName: FlowClassName.CalculateFeatures,
    });
  const secondaryResultsForPrimary = primaryRunOrchestrationId
    ? filter(
        experimentResults,
        (result) =>
          result?.experimentResultId === primaryRunResult?.experimentResultId ||
          (result?.flowClassName === FlowClassName.CalculateFeatures &&
            getPrimaryRunOrchestrationIdFromExperimentResult(result) === primaryRunOrchestrationId)
      )
    : [];
  return {
    primaryRunResult,
    secondaryResultsForPrimary,
  };
};
